import type { FC } from 'react';

import { Accordion } from '@coop/components';

import type { MenuKey, MenuNode } from '../../../hooks/useMenuState';
import type {
    ChildrenRenderBehavior,
    SideMenuExpandClickHandler,
    SideMenuLinkClickHandler,
} from './SideMenu.types';

import styles from './SideMenu.module.scss';
import SideMenuItem from './SideMenuItem';

interface MenuProps {
    currentPath: string;
    label: string;
    rootNode: MenuNode;
    activeNodesTrail: MenuKey[];
    expandedNodesTrail: MenuKey[];
    onLinkClick: SideMenuLinkClickHandler;
    onExpandClick: SideMenuExpandClickHandler;
    childrenRenderBehavior?: ChildrenRenderBehavior;
}

const SideMenu: FC<React.PropsWithChildren<MenuProps>> = ({
    currentPath,
    label,
    rootNode,
    activeNodesTrail,
    expandedNodesTrail,
    onLinkClick,
    onExpandClick,
    children,
    childrenRenderBehavior = 'onExpand',
}) => {
    const expandedNodeId = rootNode?.nodes.find((node) => expandedNodesTrail.includes(node.id))
        ?.id as string | undefined;

    return (
        <nav className={styles.SideMenu} aria-label={label}>
            <Accordion.Root collapsible type="single" asChild value={expandedNodeId}>
                <ul>
                    {rootNode?.nodes?.map((node) => (
                        <SideMenuItem
                            menuNode={node}
                            currentPath={currentPath}
                            key={node.id}
                            id={node.id}
                            url={node.url}
                            segmentUrl={node.segmentUrl}
                            name={node.name}
                            onExpandClick={onExpandClick}
                            onLinkClick={onLinkClick}
                            ancestors={node.ancestors}
                            nodes={node.nodes}
                            activeNodesTrail={activeNodesTrail}
                            expandedNodesTrail={expandedNodesTrail}
                            level={1}
                            type={node.type}
                            childrenRenderBehavior={childrenRenderBehavior}
                        />
                    ))}
                    {children}
                </ul>
            </Accordion.Root>
        </nav>
    );
};

export default SideMenu;
